import { useState } from 'react';
import SectionHeading from '@/pages/home/components/SectionHeading';
import { relationHub, relationNodes } from '@/mocks/homeData';

const HUB_X = 50;
const HUB_Y = 50;

const tagDot: Record<string, string> = {
  关键节点: 'bg-accent-500',
  沟通中枢: 'bg-primary-500',
  核心外围: 'bg-secondary-500',
  外围线索: 'bg-foreground-400',
};

interface NodeView {
  id: string;
  name: string;
  role: string;
  tag: string;
  weight: number;
  note: string;
  x: number;
  y: number;
}

function buildLine(x: number, y: number) {
  const dx = x - HUB_X;
  const dy = y - HUB_Y;
  const length = Math.sqrt(dx * dx + dy * dy);
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  return {
    left: `${HUB_X}%`,
    top: `${HUB_Y}%`,
    width: `${length}%`,
    transform: `rotate(${angle}deg)`,
    transformOrigin: 'left center',
  };
}

export default function RelationGraph() {
  const [selectedId, setSelectedId] = useState<string>(relationNodes[0].id);

  const selected: NodeView =
    selectedId === 'hub'
      ? { id: 'hub', name: relationHub.name, role: relationHub.role, tag: '事件中心', weight: 100, note: relationHub.note, x: HUB_X, y: HUB_Y }
      : relationNodes.find((n) => n.id === selectedId) ?? relationNodes[0];

  return (
    <section id="relation" className="scroll-mt-20 border-b border-background-200 bg-background-100/50">
      <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          index="04"
          eyebrow="Relationship Map"
          title="人物关系图"
          desc="把当事主体、经手人、担保方与抄送人放进同一张图，点选任意节点，即可查看该节点的关系说明与关联强度。"
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(280px,330px)_1fr] lg:gap-10">
          <div className="rounded-lg border border-background-200 bg-background-100/50 p-4 md:p-5">
            <div className="flex items-center justify-between">
              <span className="font-label text-xs tracking-[0.18em] text-foreground-500">
                KEY NODES
              </span>
              <span className="font-label text-xs text-foreground-500">{relationNodes.length + 1} 个</span>
            </div>

            <div className="mt-4 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setSelectedId('hub')}
                className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-3 text-left transition-colors ${
                  selectedId === 'hub'
                    ? 'border-primary-300 bg-primary-50'
                    : 'border-background-200 bg-background-50 hover:bg-background-100'
                }`}
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 font-heading text-xs font-bold text-background-50">
                  核心
                </span>
                <span className="flex flex-col">
                  <span className="text-sm font-medium text-foreground-950">{relationHub.name}</span>
                  <span className="font-label text-[11px] text-foreground-500">{relationHub.role}</span>
                </span>
              </button>

              {relationNodes.map((node) => (
                <button
                  key={node.id}
                  type="button"
                  onClick={() => setSelectedId(node.id)}
                  className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors ${
                    selectedId === node.id
                      ? 'border-accent-300 bg-accent-50'
                      : 'border-background-200 bg-background-50 hover:bg-background-100'
                  }`}
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300 bg-background-50 font-heading text-xs font-bold text-foreground-900">
                    {node.name}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-foreground-950">{node.role}</span>
                      <span className={`h-1.5 w-1.5 rounded-full ${tagDot[node.tag] ?? 'bg-foreground-400'}`}></span>
                    </span>
                    <span className="mt-1.5 block h-1 w-full overflow-hidden rounded-full bg-background-200">
                      <span
                        className="block h-full rounded-full bg-foreground-700"
                        style={{ width: `${node.weight}%` }}
                      ></span>
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="relative mx-auto aspect-square w-full max-w-[520px]">
              {relationNodes.map((node) => (
                <span
                  key={`line-${node.id}`}
                  className="absolute block h-px bg-background-300"
                  style={buildLine(node.x, node.y)}
                ></span>
              ))}

              <button
                type="button"
                onClick={() => setSelectedId('hub')}
                style={{ left: `${HUB_X}%`, top: `${HUB_Y}%` }}
                className={`absolute flex h-[88px] w-[88px] -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full font-heading text-xs font-bold text-background-50 transition-all md:h-[104px] md:w-[104px] ${
                  selectedId === 'hub'
                    ? 'bg-primary-500 ring-4 ring-primary-200'
                    : 'bg-primary-500/85 hover:bg-primary-500'
                }`}
              >
                核心
              </button>

              {relationNodes.map((node) => (
                <button
                  key={node.id}
                  type="button"
                  onClick={() => setSelectedId(node.id)}
                  style={{ left: `${node.x}%`, top: `${node.y}%` }}
                  className={`absolute flex h-[64px] w-[64px] -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border font-heading text-xs font-bold transition-all md:h-[76px] md:w-[76px] ${
                    selectedId === node.id
                      ? 'border-accent-400 bg-accent-50 text-accent-700 ring-2 ring-accent-200'
                      : 'border-background-300 bg-background-50 text-foreground-800 hover:border-foreground-400'
                  }`}
                >
                  {node.name}
                </button>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
              {Object.entries(tagDot).map(([tag, color]) => (
                <span key={tag} className="flex items-center gap-1.5 font-label text-[11px] text-foreground-600">
                  <span className={`h-1.5 w-1.5 rounded-full ${color}`}></span>
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-100/60 p-5">
              <div className="flex flex-wrap items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-50 font-heading text-xs font-bold text-foreground-900">
                  {selected.name}
                </span>
                <span className="flex flex-col">
                  <span className="font-heading text-base font-bold text-foreground-950">
                    {selected.name === '核心当事人' ? relationHub.name : selected.name}
                  </span>
                  <span className="font-label text-[11px] text-foreground-500">{selected.role}</span>
                </span>
                <span className="ml-auto rounded-full border border-secondary-200 bg-secondary-100 px-2.5 py-1 font-label text-[11px] text-secondary-900">
                  关联强度 {selected.weight}
                </span>
              </div>
              <p className="mt-3 text-[13px] leading-6 text-foreground-600">{selected.note}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}